import { useState, useRef, useEffect } from "react";
import { Send, MessageSquare, Loader2, Mic, MicOff } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from "./ui/dialog";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { useIsMobile } from "@/hooks/use-mobile";

interface Message {
  role: 'user' | 'assistant';
  content: string;
}

const AIChatBox = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const chatRef = useRef<HTMLDivElement>(null);
  const recognitionRef = useRef<any>(null);
  const isMobile = useIsMobile();

  useEffect(() => {
    if (chatRef.current) {
      chatRef.current.scrollTop = chatRef.current.scrollHeight;
    }
  }, [messages]);

  // Initialize speech recognition
  useEffect(() => {
    if (typeof window !== 'undefined' && ('SpeechRecognition' in window || 'webkitSpeechRecognition' in window)) {
      const SpeechRecognition = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
      recognitionRef.current = new SpeechRecognition();
      recognitionRef.current.continuous = false;
      recognitionRef.current.interimResults = false;

      recognitionRef.current.onresult = (event: any) => {
        const transcript = event.results[0][0].transcript;
        setInput(prev => prev + (prev ? ' ' : '') + transcript);
        setIsListening(false);
      };

      recognitionRef.current.onerror = (event: any) => {
        console.error('Speech recognition error:', event.error);
        setIsListening(false);
        if (event.error === 'not-allowed') {
          toast.error('Microphone access denied. Please enable microphone permissions.');
        } else {
          toast.error('Voice input failed. Please try again.');
        }
      };

      recognitionRef.current.onend = () => {
        setIsListening(false);
      };
    }
  }, []);

  const toggleVoiceInput = () => {
    if (!recognitionRef.current) {
      toast.error('Voice input is not supported on this browser.');
      return;
    }

    if (isListening) {
      recognitionRef.current.stop();
      setIsListening(false);
    } else {
      try {
        recognitionRef.current.start();
        setIsListening(true);
        toast.info('Listening... Speak now');
      } catch (error) {
        console.error('Error starting speech recognition:', error);
        toast.error('Failed to start voice input.');
      }
    }
  };

  const handleSend = async () => {
    const message = input.trim();
    if (!message || isLoading) return;

    // Validate input length
    if (message.length > 2000) {
      toast.error("Message is too long. Please keep it under 2000 characters.");
      return;
    }

    const userMessage: Message = { role: 'user', content: message };
    const newMessages = [...messages, userMessage];
    setMessages(newMessages);
    setInput("");
    setIsLoading(true);

    try {
      console.log("Sending message to AI chat...");
      const { data, error } = await supabase.functions.invoke('ai-chat', {
        body: { messages: newMessages.slice(-8) } // Keep last 8 messages for context
      });

      console.log("AI chat response:", data, error);

      if (error) {
        console.error("Supabase function error:", error);
        throw error;
      }

      const answer = data?.answer || 'Sorry, I could not generate a response.';
      const assistantMessage: Message = { role: 'assistant', content: answer };
      setMessages([...newMessages, assistantMessage]);
      
      toast.success("Response received!");

    } catch (error) {
      console.error('AI chat error:', error);
      toast.error("Unable to get a response. Please try again.");
      setMessages(newMessages);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <Button
          size="lg"
          variant="outline"
          className="glass-effect border-[hsl(var(--line))] hover:bg-white/10 hover:border-primary/50 font-bold rounded-xl px-6 py-5 text-base min-h-[46px] touch-manipulation transition-all duration-300"
        >
          <MessageSquare className="h-5 w-5 mr-2" />
          Live Agent
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[500px] max-h-[85vh] h-[600px] flex flex-col p-0 m-4">
        <DialogHeader className="p-4 border-b border-border bg-accent/10">
          <DialogTitle className="flex items-center gap-2">
            <MessageSquare className="h-5 w-5 text-accent" />
            Live Agent Chat
          </DialogTitle>
          <DialogDescription>
            Chat with our AI assistant about Neil AI robots and services
          </DialogDescription>
        </DialogHeader>

        {/* Messages */}
        <div
          ref={chatRef}
          className="flex-1 overflow-y-auto p-4 space-y-4 bg-background/50"
        >
          {messages.length === 0 && (
            <div className="text-center text-muted-foreground py-8">
              <MessageSquare className="h-12 w-12 mx-auto mb-3 opacity-50" />
              <p className="text-sm font-semibold mb-4">Hi! Chat with our AI assistant.</p>
              <div className="space-y-2 text-xs">
                <p className="font-medium">Try asking:</p>
                <button
                  onClick={() => setInput("What robots do you offer for restaurants?")}
                  className="block w-full text-left px-3 py-2 bg-muted/50 hover:bg-muted rounded-lg transition-colors"
                >
                  "What robots do you offer for restaurants?"
                </button>
                <button
                  onClick={() => setInput("Tell me about pricing and ROI")}
                  className="block w-full text-left px-3 py-2 bg-muted/50 hover:bg-muted rounded-lg transition-colors"
                >
                  "Tell me about pricing and ROI"
                </button>
                <button
                  onClick={() => setInput("What financing options are available?")}
                  className="block w-full text-left px-3 py-2 bg-muted/50 hover:bg-muted rounded-lg transition-colors"
                >
                  "What financing options are available?"
                </button>
                <button
                  onClick={() => setInput("How do I schedule a demo?")}
                  className="block w-full text-left px-3 py-2 bg-muted/50 hover:bg-muted rounded-lg transition-colors"
                >
                  "How do I schedule a demo?"
                </button>
              </div>
            </div>
          )}
          
          {messages.map((msg, idx) => (
            <div
              key={idx}
              className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
            >
              <div
                className={`max-w-[80%] px-4 py-2 rounded-2xl ${
                  msg.role === 'user'
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-muted text-foreground'
                }`}
              >
                <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
              </div>
            </div>
          ))}
          
          {isLoading && (
            <div className="flex justify-start">
              <div className="bg-muted px-4 py-2 rounded-2xl">
                <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
              </div>
            </div>
          )}
        </div>

        {/* Input */}
        <div className="p-4 border-t border-border bg-background">
          <div className="flex gap-2">
            <Input
              type="text"
              placeholder={isListening ? "Listening..." : "Type or speak your message..."}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyPress}
              disabled={isLoading}
              className="flex-1"
              aria-label="Chat message input"
            />
            <Button
              onClick={toggleVoiceInput}
              disabled={isLoading}
              size="sm"
              variant={isListening ? "default" : "outline"}
              className="px-3"
              aria-label="Voice input"
            >
              {isListening ? (
                <MicOff className="h-4 w-4 animate-pulse" />
              ) : (
                <Mic className="h-4 w-4" />
              )}
            </Button>
            <Button
              onClick={handleSend}
              disabled={isLoading || !input.trim()}
              size="sm"
              className="px-3"
              aria-label="Send message"
            >
              <Send className="h-4 w-4" />
            </Button>
          </div>
          <p className="text-xs text-muted-foreground mt-2">
            {isListening ? "🎤 Listening to your voice..." : "Type or tap mic to speak • Quick responses"}
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default AIChatBox;
